import React from "react";
import { useStaticQuery, graphql } from "gatsby";

import SoftwareLayout from "@/components/pages/Software/SoftwareLayout";
import { Stack, Typography, Unstable_Grid2 as Grid } from "@mui/material";
import Markdown from "@/components/Markdown";
import Link from "@openstack_dev/gatsby-theme-marketing-oif-core/src/components/Link";
import PropTypes from "prop-types";
import styles from "./index.module.scss";

const Title = ({ children, sx = {} }) => (
  <Typography variant="h3" sx={sx}>
    {children}
  </Typography>
);

const SubTitle = ({ children, sx = {} }) => (
  <Typography variant="h4" className={styles.subtitle} sx={sx}>
    <Markdown>{children}</Markdown>
  </Typography>
);
const Content = ({ children, sx = {} }) => (
  <Typography variant="body1" component="div" sx={sx}>
    <Markdown>{children}</Markdown>
  </Typography>
);

const genericPropTypes = {
  children: PropTypes.node.isRequired,
  sx: PropTypes.objectOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  )
};

Title.propTypes = genericPropTypes;
SubTitle.propTypes = genericPropTypes;
Content.propTypes = genericPropTypes;

const SoftwareStartPageTemplate = ({ data }) => {
  const { pageTitle, trySection, learn, deploy, nextSteps } = data;

  return (
    <SoftwareLayout>
      <Typography variant="h1">{pageTitle}</Typography>
      <hr />
      <Title>{trySection.title}</Title>
      <Grid
        container
        sx={{
          display: "flex",
          flexDirection: "row",
          flexWrap: "wrap",
          justifyContent: "stretch"
        }}
        spacing={2}
      >
        {trySection.content?.map(
          ({
            title: sectionItemTitle,
            content: sectionItemContent,
            logo,
            actions
          }) => {
            const sectionItemKey = `try-item-${sectionItemTitle}`;
            return (
              <Grid key={sectionItemKey} xs={12} md={6}>
                <SubTitle>{sectionItemTitle}</SubTitle>
                <Stack
                  direction="row"
                  spacing={2}
                  flexWrap="no-wrap"
                  useFlexGap
                  flexGrow={1}
                >
                  <Content sx={{ flexGrow: 1 }}>{sectionItemContent}</Content>
                  {logo && (
                    <div
                      style={{
                        width: "140px",
                        minWidth: "140px",
                        maxWidth: "140px",
                        flexShrink: 0,
                        flexGrow: 0,
                        marginX: 0
                      }}
                    >
                      <img
                        src={logo.publicURL}
                        alt={sectionItemTitle}
                        className={styles.logo}
                      />
                    </div>
                  )}
                </Stack>
                <Stack
                  direction="row"
                  spacing={1}
                  flexWrap="wrap"
                  useFlexGap
                  flexGrow={0}
                >
                  {actions?.map(({ label, url }) => (
                    <Link
                      key={`${sectionItemKey}-action-${label}`}
                      to={url}
                      className={styles.actionBtn}
                    >
                      {label}
                    </Link>
                  ))}
                </Stack>
              </Grid>
            );
          }
        )}
      </Grid>
      <hr />
      <Title>{learn.title}</Title>
      <Grid
        container
        sx={{
          display: "flex",
          flexDirection: "row",
          flexWrap: "wrap",
          justifyContent: "stretch"
        }}
        spacing={2}
      >
        {learn.content?.map(
          ({
            title: sectionItemTitle,
            content: sectionItemContent,
            actions
          }) => {
            const sectionItemKey = `learn-item-${sectionItemTitle}`;
            return (
              <Grid key={sectionItemKey} xs={12} md={6}>
                <SubTitle>{sectionItemTitle}</SubTitle>
                <Stack
                  direction="row"
                  spacing={2}
                  flexWrap="no-wrap"
                  useFlexGap
                  flexGrow={1}
                >
                  <Content sx={{ flexGrow: 1 }}>{sectionItemContent}</Content>
                </Stack>
                <Stack
                  direction="row"
                  spacing={1}
                  flexWrap="wrap"
                  useFlexGap
                  flexGrow={0}
                >
                  {actions?.map(({ label, url }) => (
                    <Link
                      key={`${sectionItemKey}-action-${label}`}
                      to={url}
                      className={styles.actionBtn}
                    >
                      {label}
                    </Link>
                  ))}
                </Stack>
              </Grid>
            );
          }
        )}
      </Grid>
      <hr />
      <Title>{deploy.title}</Title>
      <Content sx={{ marginBottom: 2 }}>{deploy.summary}</Content>
      <SubTitle>{deploy.distributions.title}</SubTitle>
      <Stack
        direction="row"
        spacing={1}
        flexWrap="no-wrap"
        justifyContent="flex-start"
        useFlexGap
      >
        <Content>{deploy.distributions.content}</Content>
        {deploy.distributions.logo && (
          <Stack
            style={{ minWidth: "170px", width: "170px", maxWidth: "170px" }}
            direction="column"
            justifyContent="center"
            alignItems="center"
            useFlexGap
          >
            <img
              src={deploy.distributions.logo.publicURL}
              alt=""
              className={styles.logo}
            />
          </Stack>
        )}
      </Stack>
      <Grid
        container
        sx={{
          display: "flex",
          flexDirection: "row",
          flexWrap: "wrap",
          justifyContent: "stretch",
          gap: "30px 0"
        }}
      >
        <Grid
          sx={{
            display: "flex",
            flexDirection: "column",
            paddingRight: "15px"
          }}
          xs={12}
          md={6}
        >
          <SubTitle sx={{ marginBottom: 2 }}>{deploy.howto.title}</SubTitle>
          <Stack spacing={0}>
            {deploy.howto.list.map(({ title, url }) => (
              <Link key={`howto-${title}`} to={url} className={styles.guide}>
                {title}
              </Link>
            ))}
          </Stack>
        </Grid>
        <Grid
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "stretch",
            paddingLeft: "15px"
          }}
          xs={12}
          md={6}
        >
          <div>
            <SubTitle>{deploy.companies.title}</SubTitle>
            <Content>{deploy.companies.content}</Content>
          </div>
          <div>
            <SubTitle>{deploy.download.title}</SubTitle>
            <Content>{deploy.download.content}</Content>
          </div>
        </Grid>
      </Grid>
      <hr />
      <SubTitle>{deploy.tools.title}</SubTitle>
      <Stack direction="row" spacing={2} flexWrap="no-wrap" useFlexGap>
        <Content sx={{ flexGrow: 1 }}>{deploy.tools.content}</Content>
        {deploy.tools.logo && (
          <div
            style={{
              width: "170px",
              minWidth: "170px",
              maxWidth: "170px",
              flexShrink: 0,
              flexGrow: 0,
              marginX: 0
            }}
          >
            <img
              src={deploy.tools.logo.publicURL}
              alt={deploy.tools.title}
              className={styles.logo}
            />
          </div>
        )}
      </Stack>
      <hr />
      <Title sx={{ marginBottom: 2 }}>{nextSteps.title}</Title>
      <Grid container spacing={2}>
        {nextSteps.content.map(({ title, content, actions }) => {
          const nextStepItemKey = `nextStep-item-${title}`;
          return (
            <Grid key={nextStepItemKey} xs={12} md={4}>
              <SubTitle>{title}</SubTitle>
              <Content>{content}</Content>
              <Stack
                direction="row"
                spacing={1}
                flexWrap="wrap"
                useFlexGap
                flexGrow={0}
              >
                {actions?.map(({ label, url }) => (
                  <Link
                    key={`${nextStepItemKey}-action-${label}`}
                    to={url}
                    className={styles.actionBtn}
                  >
                    {label}
                  </Link>
                ))}
              </Stack>
            </Grid>
          );
        })}
      </Grid>
    </SoftwareLayout>
  );
};

SoftwareStartPageTemplate.propTypes = {
  data: PropTypes.shape({
    pageTitle: PropTypes.string,
    trySection: PropTypes.shape({
      title: PropTypes.string,
      content: PropTypes.arrayOf(
        PropTypes.shape({
          title: PropTypes.string,
          content: PropTypes.string,
          logo: PropTypes.shape({
            publicURL: PropTypes.string
          }),
          actions: PropTypes.arrayOf(
            PropTypes.shape({
              label: PropTypes.string,
              url: PropTypes.string
            })
          )
        })
      )
    }),
    learn: PropTypes.shape({
      title: PropTypes.string,
      content: PropTypes.arrayOf(
        PropTypes.shape({
          title: PropTypes.string,
          content: PropTypes.string,
          actions: PropTypes.arrayOf(
            PropTypes.shape({
              label: PropTypes.string,
              url: PropTypes.string
            })
          )
        })
      )
    }),
    deploy: PropTypes.shape({
      title: PropTypes.string,
      summary: PropTypes.string,
      distributions: PropTypes.shape({
        title: PropTypes.string,
        content: PropTypes.string,
        logo: PropTypes.shape({
          publicURL: PropTypes.string
        })
      }),
      howto: PropTypes.shape({
        title: PropTypes.string,
        list: PropTypes.arrayOf(
          PropTypes.shape({
            title: PropTypes.string,
            url: PropTypes.string
          })
        )
      }),
      companies: PropTypes.shape({
        title: PropTypes.string,
        content: PropTypes.string
      }),
      download: PropTypes.shape({
        title: PropTypes.string,
        content: PropTypes.string
      }),
      tools: PropTypes.shape({
        title: PropTypes.string,
        content: PropTypes.string,
        logo: PropTypes.shape({
          publicURL: PropTypes.string
        })
      })
    }),
    nextSteps: PropTypes.shape({
      title: PropTypes.string,
      content: PropTypes.arrayOf(
        PropTypes.shape({
          title: PropTypes.string,
          content: PropTypes.string,
          actions: PropTypes.arrayOf(
            PropTypes.shape({
              label: PropTypes.string,
              url: PropTypes.string
            })
          )
        })
      )
    })
  }).isRequired
};

const dataQuery = graphql`
  query {
    softwareStartJson {
      pageTitle
      trySection {
        title
        content {
          title
          content
          logo {
            publicURL
          }
          actions {
            label
            url
          }
        }
      }
      learn {
        title
        content {
          title
          content
          actions {
            label
            url
          }
        }
      }
      deploy {
        title
        summary
        distributions {
          title
          content
          logo {
            publicURL
          }
        }
        howto {
          title
          list {
            title
            url
          }
        }
        companies {
          title
          content
        }
        download {
          title
          content
        }
        tools {
          title
          content
          logo {
            publicURL
          }
        }
      }
      nextSteps {
        title
        content {
          title
          content
          actions {
            label
            url
          }
        }
      }
    }
  }
`;

const SoftwareStartPage = () => {
  const { softwareStartJson } = useStaticQuery(dataQuery);

  return <SoftwareStartPageTemplate data={softwareStartJson} />;
};

export default SoftwareStartPage;
